import React, { useEffect, useState } from "react";
import { MdClose, MdError, MdInfo } from "react-icons/md";
import { FaCircleCheck } from "react-icons/fa6";
import type { Toast as ToastType } from "../contexts/ToastContext";

interface ToastProps {
  toast: ToastType;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Cast icons to proper type
  const CheckCircleIcon = FaCircleCheck as React.ComponentType<any>;
  const ErrorIcon = MdError as React.ComponentType<any>;
  const InfoIcon = MdInfo as React.ComponentType<any>;
  const CloseIcon = MdClose as React.ComponentType<any>;

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto-dismiss after 4 seconds (with animation)
    const autoDismissTimer = setTimeout(() => {
      handleClose();
    }, 4000);

    return () => clearTimeout(autoDismissTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  const getToastStyles = () => {
    const baseStyles = {
      success: {
        background: "rgba(5, 150, 105, 0.2)",
        borderColor: "rgba(5, 150, 105, 0.4)",
        iconColor: "rgba(5, 150, 105, 0.9)",
      },
      error: {
        background: "rgba(220, 38, 38, 0.2)",
        borderColor: "rgba(220, 38, 38, 0.4)",
        iconColor: "rgba(220, 38, 38, 0.9)",
      },
      info: {
        background: "rgba(75, 85, 99, 0.2)",
        borderColor: "rgba(75, 85, 99, 0.4)",
        iconColor: "rgba(148, 163, 184, 0.9)",
      },
    };

    return baseStyles[toast.type];
  };

  const getIcon = () => {
    const iconStyle = { fontSize: "20px", flexShrink: 0 };
    switch (toast.type) {
      case "success":
        return <CheckCircleIcon style={iconStyle} />;
      case "error":
        return <ErrorIcon style={iconStyle} />;
      case "info":
        return <InfoIcon style={iconStyle} />;
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className="toast"
      style={{
        minWidth: "320px",
        maxWidth: "500px",
        padding: "16px",
        borderRadius: "12px",
        background: styles.background,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        // border: `1px solid ${styles.borderColor}`,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
        color: "var(--text-primary)",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: "12px",
        transform: isExiting
          ? "translateX(400px)"
          : isVisible
          ? "translateY(0) translateX(0)"
          : "translateY(100%)",
        opacity: isExiting ? 0 : isVisible ? 1 : 0,
        transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
      }}
      role="alert"
      aria-live="polite"
    >
      <div style={{ color: styles.iconColor, marginTop: "2px" }}>
        {getIcon()}
      </div>
      <div
        style={{
          flex: 1,
          fontSize: "15px",
          lineHeight: "1.5",
          color: "var(--text-secondary)",
          wordBreak: "break-word",
        }}
      >
        {toast.message}
      </div>
      <button
        onClick={handleClose}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "6px",
          transition: "all 0.2s ease",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          e.currentTarget.style.color = "var(--text-primary)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "var(--text-muted)";
        }}
        aria-label="Close notification"
      >
        <CloseIcon style={{ fontSize: "18px" }} />
      </button>
    </div>
  );
};
